import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMediaQuery, useDisclosure } from "@chakra-ui/react";
import {
  Box,
  Button,
  Collapse,
  CloseButton,
  Flex,
  IconButton,
  Link,
  LinkBox,
  LinkOverlay,
  Menu,
  MenuList,
  MenuButton,
  MenuItem,
} from "@chakra-ui/react";
import MobilePanel from "../MobilePanel";
import SearchPanel from "../SearchPanel";
import MobileSearchPanel from "../MobileSearchPanel";
import Banner from "../Banner";
import Ad from "../Ad";
import { motion, useScroll, useTransform } from "framer-motion";
import { throttle } from 'lodash';
import { FiChevronRight, FiMenu, FiSearch } from "react-icons/fi";
import { Logo } from "../../assets/react/Logo";
import { MenuType } from "../types/navbarTypes";
import "./nav.scss";
import moment from 'moment-timezone';

// framer motion component -> built outside of component for performance
const MotionMenuList = motion(MenuList);


// secondary version of global navbar

const Navbar = () => {
  // breakpoints
  const [ isLargerThan576 ] = useMediaQuery("(min-width: 576px)");
  const [ isLargerThan768 ] = useMediaQuery("(min-width: 768px)");
  const [ isLargerThan1200 ] = useMediaQuery("(min-width: 1200px)");
  const [ isLargerThan1920 ] = useMediaQuery("(min-width: 1920px)");

  // ad banner heights
  const desktopAdHeight = 106; // 768px
  const tabletAdHeight = 70; // 576px
  const mobileAdHeight = 60; // base

  // notification banner height
  const bannerHeight = 40;

  // navbar heights
  const desktopNavHeight = 147;
  const tabletNavHeight = 92;
  const mobileNavHeight = 54;

  // opened menu handler
  const [openedMenu, setOpenedMenu] = useState("");
  const [openedSubMenu, setOpenedSubMenu] = useState("");

  // link ref to send focus to
  const linkRef = React.useRef<HTMLAnchorElement | null>(null);

  // --- Menu Dropdown Drawer Handler --- //
  const { 
    isOpen: isMenuDropdownDrawerOpen, 
    onOpen: onMenuDropdownDrawerOpen, 
    onClose: onMenuDropdownDrawerClose,
  } = useDisclosure();


  // --- Search and Hamburger Icon Handlers --- //
  const { 
    isOpen: isMobilePanelOpen, 
    onOpen: onMobilePanelOpen, 
    onClose: onMobilePanelClose 
  } = useDisclosure();

  const { 
    isOpen: isMobileSearchPanelOpen, 
    onOpen: onMobileSearchPanelOpen, 
    onClose: onMobileSearchPanelClose 
  } = useDisclosure();

  const { 
    isOpen: isSearchPanelOpen, 
    onOpen: onSearchPanelOpen, 
    onClose: onSearchPanelClose 
  } = useDisclosure();


  // handle resize scenarios
  let resizeTimer: any;
  function handleResize() {
    clearTimeout(resizeTimer);

    resizeTimer = setTimeout(() => {
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Edge case: close nav drawer on desktop when user shrinks browser window to less than 460px
      if (viewportHeight < 460 && viewportWidth > 1200) {
        setOpenedSubMenu("");
        setOpenedMenu("");
        onMenuDropdownDrawerClose();
        if (linkRef && linkRef.current) linkRef.current.focus();
      }
    }, 500)
  }

  window.addEventListener("resize", handleResize);


  // -- Set dynamic height for dropdown menu black drawer --- //
  const [ menuListHeight, setMenuListHeight ] = useState(0);

  // menu link 1 refs
  const menu1Ref = useRef<HTMLDivElement>(null);
  const menu1Submenu1Ref = useRef<HTMLDivElement>(null);
  const menu1Submenu2Ref = useRef<HTMLDivElement>(null);
  const menu1Submenu3Ref = useRef<HTMLDivElement>(null);
  const menu1Submenu4Ref = useRef<HTMLDivElement>(null);

  // menu link 2 refs
  const menu2Ref = useRef<HTMLDivElement>(null);
  // const menu2Submenu4Ref = useRef<HTMLDivElement>(null);
  const menu2Submenu7Ref = useRef<HTMLDivElement>(null);

  // menu link 3 refs
  const menu3Ref = useRef<HTMLDivElement>(null);
  const menu3Submenu2Ref = useRef<HTMLDivElement>(null);
  const menu3Submenu4Ref = useRef<HTMLDivElement>(null);

  // menu link 4 refs
  const menu4Ref = useRef<HTMLDivElement>(null);

  // menu link 7 refs
  const menu7Ref = useRef<HTMLDivElement>(null);

  const calculateMenuHeight = useCallback(( menuNumber: number, subMenuNumber: number | null ) => {
    const menus: MenuType = [
      // ignore menu index 0
      {
        menuHeight: 0,
        subMenuHeights: [
          0, // ignore submenu index 0
        ],
      },
      // Menu 1 - About Us
      {
        menuHeight: menu1Ref.current?.offsetHeight || 0, // menu 1
        subMenuHeights: [
          0, // ignore index 0
          menu1Submenu1Ref.current?.offsetHeight || 0, // submenu item 1
          menu1Submenu2Ref.current?.offsetHeight || 0, // submenu item 2
          menu1Submenu3Ref.current?.offsetHeight || 0, // submenu item 3
          menu1Submenu4Ref.current?.offsetHeight || 0, // submenu item 4
        ]
      },
      // Menu 2 - Festival
      {
        menuHeight: menu2Ref.current?.offsetHeight || 0, // menu 2
        subMenuHeights: [
          0, // ignore index 0
          0, // submenu item 1
          0, // submenu item 2
          0, // submenu item 3
          0, // submenu item 4
          0, // submenu item 5
          0, // submenu item 6
          menu2Submenu7Ref.current?.offsetHeight || 0, // submenu item 7,
          0, // submenu item 8
          0, // submenu item 9
        ]
      },
      // Menu 3 - What's On
      {
        menuHeight: menu3Ref.current?.offsetHeight || 0, // menu 3
        subMenuHeights: [
          0, // ignore index 0 
          0, // submenu item 1
          menu3Submenu2Ref.current?.offsetHeight || 0, // submenu item 2
          0, // submenu item 3
          menu3Submenu4Ref.current?.offsetHeight || 0, // submenu item 4
        ],
      },
      // Menu 4 - Talent Development
      {
        menuHeight: menu4Ref.current?.offsetHeight || 0, // menu 4,
        subMenuHeights: [
          0, // ignore index 0 
          0, // ignore
          0, // ignore
        ],
      },
      // Menu 7 - Tickets
      {
        menuHeight: menu7Ref.current?.offsetHeight || 0, // menu 7,
        subMenuHeights: [
          0, // ignore index 0 
          0, // ignore
          0, // ignore
        ],
      },
    ];

    // update menuListHeight to the largest height of any open menu or submenu
    setMenuListHeight( Math.max( 
      menus[menuNumber].menuHeight,
      subMenuNumber ? menus[menuNumber].subMenuHeights[subMenuNumber] : 0
    ));
  }, []);


  // --- Handle ad and notification banner state changes --- //

  const [isBannerOpen, setIsBannerOpen] = useState(false);
  const [isAdOpen, setIsAdOpen] = useState(false);

  const handleBannerStatus = useCallback((isBannerOpen: boolean ) => {
    setIsBannerOpen(isBannerOpen);
  }, []);

  const handleAdStatus = useCallback(( isAdOpen: boolean ) => {
    setIsAdOpen(isAdOpen);
  }, []);

  let adSpacerHeightDesktop = isAdOpen ? desktopAdHeight : 0;
  let adSpacerHeightTablet = isAdOpen ? tabletAdHeight : 0;
  let adSpacerHeightMobile = isAdOpen ? mobileAdHeight : 0;

  let navAndBannerSpacerHeightDesktop = (isBannerOpen ? bannerHeight : 0) + desktopNavHeight;
  let navAndBannerSpacerHeightTablet = (isBannerOpen ? bannerHeight : 0) + tabletNavHeight;
  let navAndBannerSpacerHeightMobile = (isBannerOpen ? bannerHeight : 0) + mobileNavHeight;


  // --- Handle Scroll Events --- //

  const [ scrollY, setScrollY ] = useState(0);
  const [ prevScrollY, setPrevScrollY ] = useState(0);

  const handleScroll = throttle(() => {
    setPrevScrollY( scrollY );
    setScrollY( window.scrollY );
  }, 100); // throttle scroll handling to once every 100 milliseconds

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    }
  }, [scrollY, prevScrollY]);


  // --- Animation Element A: shift banner ad out of screen --- //

  const { scrollY: FramerScrollY } = useScroll();
  const [navTop, setNavTop] = useState(0);
  const navRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = navRef.current;
    if (!element) return;
  }, [navRef]);

  const navYDesktop = useTransform(FramerScrollY, [navTop, navTop + 110], ["0px", "-110px"]); // 768px
  const navYTablet = useTransform(FramerScrollY, [navTop, navTop + 70], ["0px", "-70px"]); // 576px
  const navYMobile = useTransform(FramerScrollY, [navTop, navTop + 60], ["0px", "-60px"]); // base


  // --- Animation Element B: top navbar section --- //

  // initial state: before 110px
  let topNavBackgroundColor: string = "rgba(0, 0, 0, 0)";  // transparent

  // transition state: between 110px - 160px
  if ((scrollY >= 110) && (scrollY < 160)) {
    const percent = (scrollY - 110) / 50; // 50 represents the range
    topNavBackgroundColor = `rgba(0, 0, 0, ${ percent })`;  // transitioning from transparent to black  
  }
  // end state: after 160px
  else if (scrollY >= 160) {
    topNavBackgroundColor = "rgba(0, 0, 0, 1)";  // black
  }

  // --- Animation Element C: shift navbar out of screen --- //

  // if banner present add banner height to navbar top position
  const verticalBannerSpacer : number = isBannerOpen ? bannerHeight : 0;

  // initial state: navbar in viewport
  let verticalPositionNavbar: string | number = 0; // onscreen

  // while past 200px, if user scrolls down, move navbar out of viewport
  if (( scrollY > 200 ) && ( scrollY > prevScrollY )) {
    // navbar top position (navbar height + banner height) to move navbar out of viewport
    verticalPositionNavbar = `-${
      isLargerThan1920 ? (169 + verticalBannerSpacer) 
      : isLargerThan1200 ? (149 + verticalBannerSpacer) 
      : isLargerThan576 ? (95 + verticalBannerSpacer)  
      : (57 + verticalBannerSpacer) 
    }px`;

    // if any menus are open, close them
    if (openedMenu) {
      setOpenedSubMenu("");
      setOpenedMenu("");
      onMenuDropdownDrawerClose();
      if (linkRef && linkRef.current) linkRef.current.focus(); // move focus away from menu links
    }
  }

  // while past 500px if user scrolls up move navbar back to viewport
  if (( scrollY > 500 ) && ( prevScrollY > scrollY )) {
    verticalPositionNavbar = 0;
  }

  // Show ticket modal between June 14,2023 and Sep 18,2023
  const currentTime = moment().tz('America/Toronto');
  const showTicketModal = moment(currentTime).isAfter('2024-06-19T10:00', 'minute') && moment(currentTime).isBefore('2024-09-16T00:00', 'minute');

  // show tiff market link after May 16,2025 9am
  const showTiffMarketLink = moment(currentTime).isAfter('2025-05-16T08:55', 'minute');

  return (
    <Box
      bgColor="black"
      marginBottom={{ base: "0px", md: "-5px" }}
    >
      {/* A spacer to push down page to accomodate notification banner and nav space */}
      <Box
        className="noitification-banner-spacer"
        bgColor="black"
        h={{ base: `${ navAndBannerSpacerHeightMobile }px`, sm: `${ navAndBannerSpacerHeightTablet }px`, xl: `${ navAndBannerSpacerHeightDesktop }px` }}
      />

      {/* A spacer to push down nav if ad exists */}
      <Box
        className="ad-spacer"
        bgColor="black"
        h={ isLargerThan768 ? `${ adSpacerHeightDesktop }px` : isLargerThan576 ? `${ adSpacerHeightTablet }px` : `${ adSpacerHeightMobile }px` }
      />

      {/* nav */}
      <Box
        className="fixed-navbar"
        ref={ navRef }
        as={ motion.div }
        style={ isAdOpen ? (isLargerThan768 ? { y: navYDesktop } : isLargerThan576 ? { y: navYTablet } : { y: navYMobile }) : undefined }
        position="fixed"
        zIndex="1043"
        top={verticalPositionNavbar}
        left="0"
        w="100%"
        p="0"
        m="0"
        transition="all 0.35s ease"
      >
        {/* ad banner */}
        <Ad onAdStatusChange={ handleAdStatus } />

        <Box>
          <>
            {/* notification bar */}
            <Banner onBannerStatusChange={ handleBannerStatus } />

            <Box
              as="nav"
              id="navbar-outer-container"
              className="navbar-section"
              bgColor="black"
            >

              {/* Mobile & Tablet */}
              {!isLargerThan1200 &&
                <>
                  {/* Mobile - top section */}
                  <Flex
                    height={{ base:"57px", sm: "95px" }}
                    width="full"
                    alignItems="center"
                    justifyContent="space-between"
                    position="relative"
                    padding={{ base:"0px 20px", sm:"0px 40px" }}
                  >

                    <IconButton
                      className="mobile-nav-header-icon"
                      icon={ <FiSearch /> }
                      aria-label="Search"
                      fontSize={{ base:"20px", sm:"32px" }} 
                      background='none !important'
                      outline="0 !important"
                      border="0 !important"
                      marginBottom="0px"
                      onClick={onMobileSearchPanelOpen}
                    />

                    {/* TIFF logo */}
                      <LinkBox>
                        <LinkOverlay className="mobile-nav-tiff-logo"  href="/">
                          <Logo />
                        </LinkOverlay>
                      </LinkBox>

                    {/* hamburger menu */}
                    <IconButton
                      background='none !important'
                      outline="0 !important"
                      border="0 !important"
                      marginBottom="0px"
                      marginRight="0px"
                      _hover={{
                        background:'none !important'
                      }}
                      onClick={ isMobilePanelOpen ? onMobilePanelClose : onMobilePanelOpen }
                      variant="primary.accent"
                      fontSize={{base:"24px", sm:"40px"}} 
                      icon={ <FiMenu /> }
                      aria-label="Open Mobile Menu"
                    />
                  </Flex>

                  {/* Mobile - menu panel */}
                  <MobilePanel
                    isMobilePanelOpen={ isMobilePanelOpen }
                    onMobilePanelOpen={ onMobilePanelOpen } 
                    onMobilePanelClose={ onMobilePanelClose } 
                  />

                  {/* Mobile - search panel */}
                  <MobileSearchPanel
                    isMobileSearchPanelOpen={ isMobileSearchPanelOpen }
                    onMobileSearchPanelOpen={ onMobileSearchPanelOpen } 
                    onMobileSearchPanelClose={ onMobileSearchPanelClose } 
                  />
                </>
              }

              {/* Desktop */}
              {isLargerThan1200 &&
                <>
                  {/* Desktop - top section (logo and search) */}
                  <Flex
                    className="top-section-nav"
                    position="relative"
                    height="85px"
                    width="full"
                    justifyContent="center"
                    alignItems="center"
                  >

                    {/* TIFF logo */}
                    <Flex className="nav-logo" justify="center">
                      <a ref={ linkRef } href="/" aria-label="home link">
                        <Logo />
                      </a>
                    </Flex>

                    {/* close and search icon */}
                    <Flex
                      className="navbar-icons"
                      position="absolute" 
                      right="23px" 
                      top="16px"
                      direction="column"
                      gap="10px"
                    >
                      {openedMenu &&
                        <Flex className="navbar-icons">
                          <CloseButton
                            className="close-button"
                            onClick={() => {
                              setOpenedSubMenu("");
                              setOpenedMenu("");
                              if (linkRef && linkRef.current) linkRef.current.focus(); // move focus away from menu links 
                            }}
                            aria-label="Close submenu"
                            color="white"
                          />
                        </Flex>
                      }

                      <IconButton
                        className="search-button"
                        onClick={ isSearchPanelOpen ? onSearchPanelClose : onSearchPanelOpen }
                        icon={ <FiSearch /> }
                        size="navbar-desktop-icons"
                        aria-label="Open search panel"
                      />
                    </Flex>

                  </Flex>

                  {/* Desktop - bottom section (nav menu) */}
                  <Flex 
                    className="bottom-section-nav"
                    position="relative"
                    zIndex="1030"
                    h={{ base: "65px", bd3: "85px" }}
                    justify="center"
                    alignItems="center"
                    gap={{ base: "30px", xl: "50px", bd3: "58px", "3xl": "65px" }}
                    marginTop="-1px"
                    bgColor="black"
                  >

                    {/* 1st Menu Item - About Us */}
                    <Menu
                      key="menu1"
                      isOpen={ openedMenu === "menu1" }
                      onOpen={() => {
                        setOpenedMenu("menu1");
                        onMenuDropdownDrawerOpen();
                        calculateMenuHeight(1, null);
                      }}
                      onClose={() => {
                        setOpenedSubMenu("");
                        setOpenedMenu("");
                        onMenuDropdownDrawerClose();
                        if (linkRef && linkRef.current) linkRef.current.focus(); // move focus away from menu links
                      }}
                      autoSelect={ false }
                      variant="main-nav"
                      placement="bottom-start"
                      offset={ isLargerThan1920 ? [ 0, 33 ] : [ 0, 23] }
                      preventOverflow={ false }
                    >
                      {({ isOpen }) => (
                        <>
                          <MenuButton
                            fontSize={{ base: "16px !important", bd3: "22px !important" }}
                            borderBottomWidth="5px !important"
                            borderBottomColor={ isOpen ? "#FFF !important" : "transparent !important" }
                          >
                            About Us
                          </MenuButton>

                          <MotionMenuList
                            ref={ menu1Ref }
                            display="flex"
                            flexDirection="column"
                            alignItems="stretch"
                            initial={{ opacity: 0 }}
                            animate={ isOpen ? { opacity: 1 } : { opacity: 0 }}
                            transition={ isOpen ? { duration: 0.7 } : { duration: 0 }}
                          >

                            {/* 2nd level - link 1 */}
                            <Menu
                              key="submenu1-1"
                              isOpen={ openedSubMenu === "submenu1-1" }
                              onOpen={() => {
                                calculateMenuHeight(1, 1);
                                setOpenedSubMenu("submenu1-1");
                              }}
                              onClose={() => {
                                calculateMenuHeight(1, null);
                                setOpenedSubMenu("");
                              }}
                              variant="sub-nav"
                              placement="right-start"
                              offset={isLargerThan1920 ? [0, 30] : [0, 25]}
                              preventOverflow={ false }
                            >
                              {({ isOpen: isSubOpen1 }) => (
                                <>
                                  <MenuButton
                                    as={ Button }
                                    size="navbar-subnav"
                                    lineHeight={"24px"}
                                    borderBottom="1px solid white !important"
                                    rightIcon={<FiChevronRight />}
                                    iconSpacing="100%"
                                    fontSize={{ base: "16px", bd3: "20px" }}
                                    padding={{ base: "12px 12px 12px 16px !important", bd3: "16px 12px 16px 18px !important" }}
                                  >
                                    Who We Are
                                  </MenuButton>

                                  <MotionMenuList
                                    ref={ menu1Submenu1Ref }
                                    initial={{ opacity: 0 }}
                                    animate={ isSubOpen1 ? { opacity: 1 } : { opacity: 0 }}
                                    transition={ isSubOpen1 ? { duration: 0.7 } : { duration: 0 }}
                                  >
                                    {/* 3rd level - link 1 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/mission"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Our Mission
                                    </MenuItem>

                                    {/* 3rd level - link 2 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/land-acknowledgement"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Land Acknowledgement
                                    </MenuItem>

                                    {/* 3rd level - link 3 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/about"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      TIFF Staff {"&"} Board
                                    </MenuItem>

                                    {/* 3rd level - link 4 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/code-of-conduct"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Code of Conduct
                                    </MenuItem>

                                    {/* 3rd level - link 5 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/partnerships"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Partnerships
                                    </MenuItem>

                                  </MotionMenuList>
                                </>
                              )}
                            </Menu>

                            {/* 2nd level - link 2 */}
                            <Menu
                              key="submenu1-2"
                              isOpen={ openedSubMenu === "submenu1-2" }
                              onOpen={() => {
                                calculateMenuHeight(1, 2);
                                setOpenedSubMenu("submenu1-2");
                              }}
                              onClose={() => {
                                calculateMenuHeight(1, null);
                                setOpenedSubMenu("");
                              }}
                              variant="sub-nav"
                              placement="right-start"
                              offset={isLargerThan1920 ? [-57, 30] : [-49, 25]}
                              preventOverflow={ false }
                            >
                              {({ isOpen: isSubOpen2 }) => (
                                <>
                                  <MenuButton
                                    as={Button}
                                    size="navbar-subnav"
                                    lineHeight={"24px"}
                                    borderBottom="1px solid white !important"
                                    rightIcon={<FiChevronRight />}
                                    iconSpacing="100%"
                                    fontSize={{ base: "16px", bd3: "20px" }}
                                    padding={{ base: "12px 12px 12px 16px !important", bd3: "16px 12px 16px 18px !important" }}
                                  >
                                    Our Impact
                                  </MenuButton>

                                  <MotionMenuList
                                    ref={ menu1Submenu2Ref }
                                    initial={{ opacity: 0 }}
                                    animate={ isSubOpen2 ? { opacity: 1 } : { opacity: 0 }}
                                    transition={ isSubOpen2 ? { duration: 0.7 } : { duration: 0 }}
                                  >
                                    {/* 3rd level - link 1 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/seniors"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Silver Screenings
                                    </MenuItem>

                                    {/* 3rd level - link 2 */}
                                    <MenuItem
                                      as={ Link }
                                      href="https://filmcircuit.tiff.net/"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      TIFF Film Circuit
                                    </MenuItem>

                                    {/* 3rd level - link 3 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/shareherjourney"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Share Her Journey
                                    </MenuItem>

                                    {/* 3rd level - link 4 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/everystory"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Every Story
                                    </MenuItem>

                                    {/* 3rd level - link 5 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/partner-festivals"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Partner Festivals
                                    </MenuItem>

                                    {/* 3rd level - link 6 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/community-and-access"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Community Impact
                                    </MenuItem>

                                  </MotionMenuList>
                                </>
                              )}
                            </Menu>

                            {/* 2nd level - link 3 */}
                            <MenuItem
                              as={ Link }
                              href="/accessibility-at-tiff"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Accessibility
                            </MenuItem>

                            {/* 2nd level - link 4 */}
                            <Menu
                              key="submenu1-3"
                              isOpen={ openedSubMenu === "submenu1-3" }
                              onOpen={() => {
                                calculateMenuHeight(1, 3);
                                setOpenedSubMenu("submenu1-3");
                              }}
                              onClose={() => {
                                calculateMenuHeight(1, null);
                                setOpenedSubMenu("");
                              }}
                              variant="sub-nav"
                              placement="right-start"
                              offset={ isLargerThan1920 ? [ -171, 30 ] : [ -147, 25 ] }
                              preventOverflow={ false }
                            >
                              {({ isOpen: isSubOpen3 }) => (
                                <>
                                  <MenuButton
                                    as={ Button }
                                    size="navbar-subnav"
                                    lineHeight={"24px"}
                                    borderBottom="1px solid white !important"
                                    rightIcon={<FiChevronRight />}
                                    iconSpacing="100%"
                                    fontSize={{ base: "16px", bd3: "20px" }}
                                    padding={{ base: "12px 12px 12px 16px !important", bd3: "16px 12px 16px 18px !important" }}
                                  >
                                    Visit Us
                                  </MenuButton>

                                  <MotionMenuList
                                    ref={ menu1Submenu3Ref }
                                    initial={{ opacity: 0 }}
                                    animate={ isSubOpen3 ? { opacity: 1 } : { opacity: 0 }}
                                    transition={ isSubOpen3 ? { duration: 0.7 } : { duration: 0 }}
                                  >
                                    {/* 3rd level - link 1 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/getting-here"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Getting Here
                                    </MenuItem>

                                    {/* 3rd level - link 2 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/hotel-partners"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Hotel Partners
                                    </MenuItem>

                                  </MotionMenuList>
                                </>
                              )}
                            </Menu>

                            {/* 2nd level - link 5 */}
                            <Menu
                              key="submenu1-4"
                              isOpen={ openedSubMenu === "submenu1-4" }
                              onOpen={() => {
                                calculateMenuHeight(1, 4);
                                setOpenedSubMenu("submenu1-4");
                              }}
                              onClose={() => {
                                calculateMenuHeight(1, null);
                                setOpenedSubMenu("");
                              }}
                              variant="sub-nav"
                              placement="right-start"
                              offset={isLargerThan1920 ? [-228, 30] : [-196, 25]}
                              preventOverflow={ false }
                            >
                              {({ isOpen: isSubOpen4 }) => (
                                <>
                                  <MenuButton
                                    as={Button}
                                    size="navbar-subnav"
                                    lineHeight={"24px"}
                                    rightIcon={<FiChevronRight />}
                                    iconSpacing="100%"
                                    fontSize={{ base: "16px", bd3: "20px" }}
                                    padding={{ base: "12px 12px 12px 16px !important", bd3: "16px 12px 16px 18px !important" }}
                                  >
                                    Support TIFF
                                  </MenuButton>

                                  <MotionMenuList
                                    ref={ menu1Submenu4Ref }
                                    initial={{ opacity: 0 }}
                                    animate={ isSubOpen4 ? { opacity: 1 } : { opacity: 0 }}
                                    transition={ isSubOpen4 ? { duration: 0.7 } : { duration: 0 }}
                                  >
                                    {/* 3rd level - link 1 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/support"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Donate
                                    </MenuItem>

                                    {/* 3rd level - link 2 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/volunteer"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Volunteer
                                    </MenuItem>

                                  </MotionMenuList>
                                </>
                              )}
                            </Menu>

                          </MotionMenuList>
                        </>
                      )}
                    </Menu>

                    {/* 2nd Menu Item - Festival */}
                    <Menu
                      key="menu2"
                      isOpen={ openedMenu === "menu2" }
                      onOpen={() => {
                        setOpenedMenu("menu2");
                        onMenuDropdownDrawerOpen();
                        calculateMenuHeight(2, null);
                      }}
                      onClose={() => {
                        setOpenedSubMenu("");
                        setOpenedMenu("");
                        onMenuDropdownDrawerClose();
                        if (linkRef && linkRef.current) linkRef.current.focus(); // move focus away from menu links
                      }}
                      autoSelect={ false }
                      variant="main-nav"
                      placement="bottom-start"
                      offset={ isLargerThan1920 ? [ 0, 33 ] : [ 0, 23] }
                    >
                      {({ isOpen }) => (
                        <>
                          <MenuButton
                            fontSize={{ base: "16px !important", bd3: "22px !important" }}
                            borderBottomWidth="5px !important"
                            borderBottomColor={ isOpen ? "#FFF !important" : "transparent !important" }
                          >
                            Festival
                          </MenuButton>

                          <MotionMenuList
                            ref={ menu2Ref }
                            display="flex"
                            flexDirection="column"
                            alignItems="stretch"
                            initial={{ opacity: 0 }}
                            animate={ isOpen ? { opacity: 1 } : { opacity: 0 }}
                            transition={ isOpen ? { duration: 0.7 } : { duration: 0 }}
                          >

                            {/* 2nd level - link 1 */}
                            <MenuItem
                              as={ Link }
                              href="/about-the-festival"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              About TIFF 50
                            </MenuItem>

                            {showTiffMarketLink && 
                              <MenuItem
                                as={ Link }
                                href="/themarket"
                                borderBottom="1px solid white !important"
                                fontSize={{ base: "16px", bd3: "20px" }}
                                padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                              >
                                About TIFF: The Market
                              </MenuItem>
                            }

                            {/* 2nd level - link 2 */}
                            <MenuItem
                              as={ Link }
                              href="/submissions"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Submissions
                            </MenuItem>

                            {/* 2nd level - link 3 */}
                            {/* <MenuItem
                              as={ Link }
                              href="/films"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Films & Events List
                            </MenuItem> */}

                            {/* 2nd level - link 4 */}
                            {/* <MenuItem
                              as={ Link }
                              href="/films?schedule"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Schedule
                            </MenuItem> */}

                            {/* 2nd level - link 5 */}
                            {/* <MenuItem
                              as={ Link }
                              href="/vote"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              People's Choice Award
                            </MenuItem> */}

                            {/* <Menu
                              key="submenu2-4"
                              isOpen={ openedSubMenu === "submenu2-4" }
                              onOpen={() => {
                                calculateMenuHeight(2, 4);
                                setOpenedSubMenu("submenu2-4");
                              }}
                              onClose={() => {
                                calculateMenuHeight(2, null);
                                setOpenedSubMenu("");
                              }}
                              variant="sub-nav"
                              placement="right-start"
                              offset={ isLargerThan1920 ? [-171, 30] : [-132, 25]}
                              preventOverflow={ false }
                            >
                              {({ isOpen: isSubOpen2 }) => (
                                <>
                                  <MenuButton
                                    as={ Button }
                                    size="navbar-subnav"
                                    lineHeight={"24px"}
                                    borderBottom="1px solid white !important"
                                    rightIcon={<FiChevronRight />}
                                    iconSpacing="100%"
                                    fontSize={{ base: "16px", bd3: "20px" }}
                                    padding={{ base: "12px 12px 12px 16px !important", bd3: "16px 12px 16px 18px !important" }}
                                  >
                                    Plan Your Visit
                                  </MenuButton>

                                  <MotionMenuList
                                    ref={ menu2Submenu4Ref }
                                    initial={{ opacity: 0 }}
                                    animate={ isSubOpen2 ? { opacity: 1 } : { opacity: 0 }}
                                    transition={ isSubOpen2 ? { duration: 0.7 } : { duration: 0 }}
                                  >
                                    
                                    <MenuItem
                                      as={ Link }
                                      href="/visit?tab=Venue#"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Venues
                                    </MenuItem>

                                    
                                    <MenuItem
                                      as={ Link }
                                      href="/getting-here"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Getting Here
                                    </MenuItem>

                                    
                                    <MenuItem
                                      as={ Link }
                                      href="/hotel-partners"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Hotel Partners
                                    </MenuItem>

                                    
                                    <MenuItem
                                      as={ Link }
                                      href="/about-tiff-23?tab=tickets#"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Ticket Information
                                    </MenuItem>

                                    
                                    <MenuItem
                                      as={ Link }
                                      href="/accessibility-at-tiff"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Accessibility
                                    </MenuItem>

                                  </MotionMenuList>
                                </>
                              )}
                            </Menu> */}

                            {/* 2nd level - link 6 */}
                            <MenuItem
                              as={ Link }
                              href="/programmers"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Meet the Programmers
                            </MenuItem>

                            {/* 2nd level - link 7 */}
                            <Menu
                              key="submenu2-7"
                              isOpen={ openedSubMenu === "submenu2-7" }
                              onOpen={() => {
                                calculateMenuHeight(2, 7);
                                setOpenedSubMenu("submenu2-7");
                              }}
                              onClose={() => {
                                calculateMenuHeight(2, null);
                                setOpenedSubMenu("");
                              }}
                              variant="sub-nav"
                              placement="right-start"
                              offset={showTiffMarketLink ? (isLargerThan1920 ? [-228, 30] : [-196, 25]) : ( isLargerThan1920 ?  [-171, 30] : [-147, 25 ]) }
                              preventOverflow={ false }
                            >
                              {({ isOpen: isSubOpen7 }) => (
                                <>
                                  <MenuButton
                                    as={Button}
                                    size="navbar-subnav"
                                    lineHeight={"24px"}
                                    borderBottom="1px solid white !important"
                                    rightIcon={<FiChevronRight />}
                                    iconSpacing="100%"
                                    fontSize={{ base: "16px", bd3: "20px" }}
                                    padding={{ base: "12px 12px 12px 16px !important", bd3: "16px 12px 16px 18px !important" }}
                                  >
                                    Press {"&"} Industry
                                  </MenuButton>

                                  <MotionMenuList
                                    ref={ menu2Submenu7Ref }
                                    initial={{ opacity: 0 }}
                                    animate={ isSubOpen7 ? { opacity: 1 } : { opacity: 0 }}
                                    transition={ isSubOpen7 ? { duration: 0.7 } : { duration: 0 }}
                                  >
                                    {/* 3rd level - link 1 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/industry-fest2023?tab=conference"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Industry Conference
                                    </MenuItem>

                                    {/* 3rd level - link 2 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/industry-fest2023"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Industry Accreditation {"&"} Resources
                                    </MenuItem>

                                    {/* 3rd level - link 3 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/press"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Press Accreditation {"&"} Resources
                                    </MenuItem>

                                    {/* 3rd level - link 4 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/industry-promote-screen-advertise"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Promote, Screen, Advertise
                                    </MenuItem>

                                  </MotionMenuList>
                                </>
                              )}
                            </Menu>

                            {/* 2nd level - link 8 */}
                            <MenuItem
                              as={ Link }
                              href="/tribute-awards"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              TIFF Tribute Awards
                            </MenuItem>

                             {/* 2nd level - link 9 */}
                            <MenuItem
                              as={ Link }
                              href="/awards"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >  
                              Awards
                            </MenuItem>

                          </MotionMenuList>
                        </>
                      )}
                    </Menu>

                    {/* 3rd Menu Item - What's On */}
                    <Menu
                      key="menu3"
                      isOpen={ openedMenu === "menu3" }
                      onOpen={() => {
                        setOpenedMenu("menu3");
                        onMenuDropdownDrawerOpen();
                        calculateMenuHeight(3, null);
                      }}
                      onClose={()=> {
                        setOpenedSubMenu("");
                        setOpenedMenu("");
                        onMenuDropdownDrawerClose();
                        if (linkRef && linkRef.current) linkRef.current.focus(); // move focus away from menu links
                      }}
                      autoSelect={ false }
                      variant="main-nav"
                      placement="bottom-start"
                      offset={ isLargerThan1920 ? [ 0, 33 ] : [ 0, 23] }
                    >
                      {({ isOpen }) => (
                        <>
                          <MenuButton
                            fontSize={{ base: "16px !important", bd3: "22px !important" }}
                            borderBottomWidth="5px !important"
                            borderBottomColor={ isOpen ? "#FFF !important" : "transparent !important" }
                          >
                            What's On
                          </MenuButton>

                          <MotionMenuList
                            ref={ menu3Ref }
                            display="flex"
                            flexDirection="column"
                            alignItems="stretch"
                            initial={{ opacity: 0 }}
                            animate={ isOpen ? { opacity: 1 } : { opacity: 0 }}
                            transition={ isOpen ? { duration: 0.7 } : { duration: 0 }}
                          >

                            {/* 2rd level - link 1 */}
                            <MenuItem
                              as={ Link }
                              href="/calendar"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Now Playing
                            </MenuItem>

                            {/* 2nd level - link 2 */}
                            <Menu
                              key="submenu3-2"
                              isOpen={ openedSubMenu === "submenu3-2" }
                              onOpen={() => {
                                calculateMenuHeight(3, 2);
                                setOpenedSubMenu("submenu3-2");
                              }}
                              onClose={() => {
                                calculateMenuHeight(3, null);
                                setOpenedSubMenu("");
                              }}
                              variant="sub-nav"
                              placement="right-start"
                              offset={ isLargerThan1920 ? [ -57, 30 ] : [ -49, 25 ] }
                              preventOverflow={ false }
                            >
                              {({ isOpen: isSubOpen2 }) => (
                                <>
                                  <MenuButton
                                    as={ Button }
                                    size="navbar-subnav"
                                    lineHeight={"24px"}
                                    borderBottom="1px solid white !important"
                                    rightIcon={<FiChevronRight />}
                                    iconSpacing="100%"
                                    fontSize={{ base: "16px", bd3: "20px" }}
                                    padding={{ base: "12px 12px 12px 16px !important", bd3: "16px 12px 16px 18px !important" }}
                                  >
                                    Programming
                                  </MenuButton>

                                  <MotionMenuList
                                    ref={ menu3Submenu2Ref }
                                    initial={{ opacity: 0 }}
                                    animate={ isSubOpen2 ? { opacity: 1 } : { opacity: 0 }}
                                    transition={ isSubOpen2 ? { duration: 0.7 } : { duration: 0 }}
                                  >
                                    {/* 3rd level - link 1 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/calendar?newreleases"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      New Releases
                                    </MenuItem>

                                    {/* 3rd level - link 2 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/cinematheque"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      TIFF Cinematheque
                                    </MenuItem>

                                    {/* 3rd level - link 3 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/nextwave"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      TIFF Next Wave
                                    </MenuItem>

                                    {/* 3rd level - link 4 */}
                                        <MenuItem
                                          as={ Link }
                                          href="/smc"
                                          borderBottom="1px solid white !important"
                                          fontSize={{ base: "16px", bd3: "20px" }}
                                          padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                        >
                                          Secret Movie Club
                                        </MenuItem>

                                    {/* 3rd level - link 5 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/icc"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      International Cinema Cafe
                                    </MenuItem>

                                    {/* 3rd level - link 6 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/canadas-top-ten"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Canada's Top Ten
                                    </MenuItem>

                                    {/* 3rd level - link 6 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/industry-yearround"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Industry Year-Round
                                    </MenuItem>

                                  </MotionMenuList>
                                </>
                              )}
                            </Menu>

                            {/* 2nd level - link 3 */}
                            <MenuItem
                              as={ Link }
                              href="/varda"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Varda Café-Bar
                            </MenuItem>

                            {/* 2nd level - link 4 */}
                            <Menu
                              key="submenu3-4"
                              isOpen={ openedSubMenu === "submenu3-4" }
                              onOpen={() => {
                                calculateMenuHeight(3, 4);
                                setOpenedSubMenu("submenu3-4");
                              }}
                              onClose={() => {
                                calculateMenuHeight(3, null);
                                setOpenedSubMenu("");
                              }}
                              variant="sub-nav"
                              placement="right-start"
                              offset={ isLargerThan1920 ? [-171, 30] : [-147, 25]}
                              preventOverflow={ false }
                            >
                              {({ isOpen: isSubOpen5 }) => (
                                <>
                                  <MenuButton 
                                    as={Button} 
                                    size="navbar-subnav"
                                    lineHeight={"24px"}
                                    borderBottom="1px solid white !important"
                                    rightIcon={<FiChevronRight />}
                                    iconSpacing="100%"
                                    fontSize={{ base: "16px", bd3: "20px" }}
                                    padding={{ base: "12px 12px 12px 16px !important", bd3: "16px 12px 16px 18px !important" }}
                                  >
                                    The Library
                                  </MenuButton>

                                  <MotionMenuList
                                    ref={ menu3Submenu4Ref }
                                    initial={{ opacity: 0 }}
                                    animate={ isSubOpen5 ? { opacity: 1 } : { opacity: 0 }}
                                    transition={ isSubOpen5 ? { duration: 0.7 } : { duration: 0 }}
                                  >
                                    {/* 3rd level - link 1 */}
                                    <MenuItem
                                      as={ Link }
                                      href="/library"
                                      borderBottom="1px solid white !important"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Visit
                                    </MenuItem>

                                    {/* 3rd level - link 2 */}
                                    <MenuItem
                                      as={ Link }
                                      href="http://collection.tiff.net/mwebcgi/mweb?request=advform"
                                      fontSize={{ base: "16px", bd3: "20px" }}
                                      padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                    >
                                      Online
                                    </MenuItem>

                                  </MotionMenuList>
                                </>
                              )}
                            </Menu>

                            {/* 2rd level - link 6 */}
                            <MenuItem
                              as={ Link }
                              href="/events"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Book an Event
                            </MenuItem>

                          </MotionMenuList>
                        </>
                      )}
                    </Menu>

                    {/* 4th Menu Item - Talent Development */}
                    <Menu
                      key="menu4"
                      isOpen={ openedMenu === "menu4" }
                      onOpen={() => {
                        setOpenedMenu("menu4");
                        onMenuDropdownDrawerOpen();
                        calculateMenuHeight(4, null);
                      }}
                      onClose={() => {
                        setOpenedSubMenu("");
                        setOpenedMenu("");
                        onMenuDropdownDrawerClose();
                        if (linkRef && linkRef.current) linkRef.current.focus(); // move focus away from menu links
                      }}
                      autoSelect={ false }
                      variant="main-nav"
                      placement="bottom-start"
                      offset={ isLargerThan1920 ? [ 0, 33 ] : [ 0, 23] }
                    >
                      {({ isOpen }) => (
                        <>
                          <MenuButton
                            fontSize={{ base: "16px !important", bd3: "22px !important" }}
                            borderBottomWidth="5px !important"
                            borderBottomColor={ isOpen ? "#FFF !important" : "transparent !important" }
                          >
                            Talent Development
                          </MenuButton>

                          <MotionMenuList
                            ref={ menu4Ref }
                            display="flex"
                            flexDirection="column"
                            alignItems="stretch"
                            initial={{ opacity: 0 }}
                            animate={ isOpen ? { opacity: 1 } : { opacity: 0 }}
                            transition={ isOpen ? { duration: 0.7 } : { duration: 0 }}
                          >

                            {/* 2rd level - link 1 */}
                            <MenuItem
                              as={ Link }
                              href="/industry-talent-development"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Talent Programmes
                            </MenuItem>

                            {/* 2rd level - link 2 */}
                            <MenuItem
                              as={ Link }
                              href="/industry-alumni"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Talent Alumni
                            </MenuItem>

                          </MotionMenuList>
                        </>
                      )}
                    </Menu>

                    {/* 5th Menu Item - Become a Member */}
                    <Link href="/membership" size="navbar-main">
                      Become a Member
                    </Link>

                    {/* 6th Menu Item - TIFF Shop */}
                    <Link href="https://shop.tiff.net" size="navbar-main">
                      TIFF Shop
                    </Link>

                    {/* 7th Menu Item - Tickets */}
                    <Menu
                      key="menu7"
                      isOpen={ openedMenu === "menu7" }
                      onOpen={() => {
                        setOpenedMenu("menu7");
                        onMenuDropdownDrawerOpen();
                        calculateMenuHeight(4, null);
                      }}
                      onClose={() => {
                        setOpenedSubMenu("");
                        setOpenedMenu("");
                        onMenuDropdownDrawerClose();
                        if (linkRef && linkRef.current) linkRef.current.focus(); // move focus away from menu links
                      }}
                      autoSelect={ false }
                      variant="main-nav"
                      placement="bottom-start"
                      offset={ isLargerThan1920 ? [ 0, 33 ] : [ 0, 23] }
                    >
                      {({ isOpen }) => (
                        <>
                          <MenuButton
                            fontSize={{ base: "16px !important", bd3: "22px !important" }}
                            borderBottomWidth="5px !important"
                            borderBottomColor={ isOpen ? "#FFF !important" : "transparent !important" }
                          >
                            Tickets
                          </MenuButton>

                          <MotionMenuList
                            ref={ menu4Ref }
                            display="flex"
                            flexDirection="column"
                            alignItems="stretch"
                            initial={{ opacity: 0 }}
                            animate={ isOpen ? { opacity: 1 } : { opacity: 0 }}
                            transition={ isOpen ? { duration: 0.7 } : { duration: 0 }}
                          >

                            {/* 2rd level - link 1 */}
                            <MenuItem
                              as={ Link }
                              href="/ticket-pricing"
                              borderBottom="1px solid white !important"
                              fontSize={{ base: "16px", bd3: "20px" }}
                              padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                            >
                              Ticket Pricing
                            </MenuItem>

                            {/* 2rd level - link 2 */}
                            {showTicketModal ? 
                              <MenuItem
                                as={ Button }
                                onClick={() => { $('#festival-buy-tickets-modal').modal(); }}
                                aria-label="Open tickets modal"
                                size="navbar-main"
                                fontSize={{ base: "16px", bd3: "20px" }}
                                padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                                justifyContent="start"
                              >
                                Get Tickets
                              </MenuItem>
                              :
                              <MenuItem
                                as={ Link }
                                href="https://am.ticketmaster.com/tiff/"
                                fontSize={{ base: "16px", bd3: "20px" }}
                                padding={{ base: "12px 12px 12px 8px !important", bd3: "16px 12px 16px 8px !important" }}
                              >
                                Get Tickets
                                <span className="material-symbols-outlined" style={{ fontSize: "medium", marginLeft: "4px" }}>open_in_new</span>
                              </MenuItem>
                            }
                          
                          </MotionMenuList>
                        </>
                      )}
                    </Menu>

                  </Flex>

                  {/* menu dropdown drawer */}
                  <Collapse
                    className="menu-dropdown-drawer"
                    in={ isMenuDropdownDrawerOpen }
                  >
                    <Box
                      paddingBottom={ `${( menuListHeight + 70 )}px` }
                      style={{ 
                        maxHeight: isMenuDropdownDrawerOpen ? "700px" : "0px"
                      }}
                      bg="black"
                    >
                    </Box>
                  </Collapse>
                </>
              }
            </Box>
          </>
        </Box>

        {/* search panel desktop (hidden) */}
        <SearchPanel
          isSearchPanelOpen={ isSearchPanelOpen }
          onSearchPanelOpen={ onSearchPanelOpen }
          onSearchPanelClose={ onSearchPanelClose }
        />
      </Box>
  
    </Box>
  )
}

export default Navbar;
